import type { PublicPlan } from "@/lib/checkout.functions";

const GOLD = "#C8A84B";

function formatPrice(cents: number, currency: string) {
  if (cents <= 0) return "Custom";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

interface Props {
  plans: PublicPlan[];
  value: string | null;
  onChange: (planId: string) => void;
}

export function PlanCards({ plans, value, onChange }: Props) {
  // /agency-bundle owns the two self-serve agency plans. Hide them from
  // the generic /checkout grid so the layout stays a clean 3-up.
  const visiblePlans = plans.filter(
    (p) => p.id !== "agency_bundle" && p.id !== "agency_monthly",
  );
  return (
    <div className="grid gap-4 md:grid-cols-3">
      {visiblePlans.map((plan) => {
        const selected = value === plan.id;
        const isPro = plan.id === "pro";
        return (
          <button
            key={plan.id}
            type="button"
            onClick={() => onChange(plan.id)}
            aria-pressed={selected}
            className="group relative flex h-full flex-col rounded-2xl border bg-card/30 p-6 text-left transition-all"
            style={{
              borderColor: selected ? GOLD : "var(--color-border)",
              boxShadow: selected ? `0 0 0 1px ${GOLD}` : undefined,
            }}
          >
            {isPro && (
              <span
                className="absolute -top-3 left-6 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]"
                style={{ background: GOLD, color: "#0a0a0a" }}
              >
                Most popular
              </span>
            )}
            <div className="flex items-baseline justify-between">
              <h3 className="font-display text-xl font-semibold text-foreground">
                {plan.name}
              </h3>
              <div className="text-right">
                <p className="font-display text-2xl font-semibold text-foreground">
                  {formatPrice(plan.price_cents, plan.currency)}
                </p>
                {plan.price_cents > 0 && (
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    one-time
                  </p>
                )}
              </div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">{plan.description}</p>
            <ul className="mt-5 flex-1 space-y-2 text-sm text-foreground/90">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <span
                    aria-hidden
                    className="mt-[7px] inline-block size-1.5 shrink-0 rounded-full"
                    style={{ background: GOLD }}
                  />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {plan.is_contact_sales
                ? "Contact us"
                : plan.delivery_label || `Delivered in ~${plan.eta_days} business days`}
            </p>
            <div
              className="mt-5 inline-flex items-center justify-center rounded-full border px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] transition-colors"
              style={{
                borderColor: selected ? GOLD : "var(--color-border)",
                color: selected ? GOLD : "var(--color-foreground)",
              }}
            >
              {selected ? "Selected" : "Select"}
            </div>
          </button>
        );
      })}
    </div>
  );
}